import React, { useState, useEffect } from 'react';
import { trainingAPI, trainingTypesAPI } from '../../services/api';
import { formatDate, formatTime } from '../../utils/dateTimeHelpers';
import '../../styles/AdminStyles.css';

const KNOWN_TYPE_BADGES = ['בר', 'מכשיר', 'מזרן', 'בהריון'];

function trainingTypeBadgeClass(name) {
  return KNOWN_TYPE_BADGES.includes(name)
    ? `admin-badge admin-badge-${name}`
    : 'admin-badge admin-badge-dynamic-type';
}

function AdminSessions() {
  const [sessions, setSessions] = useState([]);
  const [trainingTypes, setTrainingTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [filterDate, setFilterDate] = useState('');
  const [formData, setFormData] = useState({
    training_date: '',
    training_time: '',
    training_type: 'מכשיר',
    max_participants: 5,
    is_recurring: false,
    end_date: '',
    weekdays: [], // Empty by default - user must select days
  });

  useEffect(() => {
    fetchSessions();
  }, []);

  useEffect(() => {
    trainingTypesAPI
      .getActive()
      .then((rows) => setTrainingTypes(Array.isArray(rows) ? rows : []))
      .catch(() => setTrainingTypes([]));
  }, []);

  const defaultTrainingTypeName = () =>
    trainingTypes.length > 0 ? trainingTypes[0].name : 'מכשיר';

  const fetchSessions = async () => {
    try {
      setLoading(true);
      const data = await trainingAPI.getSessions();
      setSessions(data);
    } catch (error) {
      console.error('Error fetching sessions:', error);
      alert('שגיאה בטעינת אימונים');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (session = null) => {
    if (session) {
      setEditingSession(session);
      setFormData({
        training_date: session.training_date,
        training_time: session.training_time.substring(0, 5),
        training_type: session.training_type,
        max_participants: session.max_participants,
        is_recurring: false,
        end_date: '',
        weekdays: [],
      });
    } else {
      setEditingSession(null);
      setFormData({
        training_date: '',
        training_time: '',
        training_type: defaultTrainingTypeName(),
        max_participants: 5,
        is_recurring: false,
        end_date: '',
        weekdays: [],
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingSession(null);
    setFormData({
      training_date: '',
      training_time: '',
      training_type: defaultTrainingTypeName(),
      max_participants: 5,
      is_recurring: false,
      end_date: '',
      weekdays: [],
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const generateRecurringDates = (startDate, endDate, weekdays) => {
    const dates = [];
    const current = new Date(startDate);
    const end = new Date(endDate);

    while (current <= end) {
      if (weekdays.includes(current.getDay())) {
        dates.push(current.toISOString().split('T')[0]);
      }
      current.setDate(current.getDate() + 1);
    }

    return dates;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingSession) {
        // Update existing session
        await trainingAPI.updateSession(
          editingSession.training_date,
          editingSession.training_time,
          {
            training_type: formData.training_type,
            max_participants: formData.max_participants,
          }
        );
        alert('האימון עודכן בהצלחה');
      } else if (formData.is_recurring) {
        // Create recurring sessions
        if (!formData.end_date) {
          alert('נא לבחור תאריך סיום לאימונים חוזרים');
          return;
        }

        if (formData.weekdays.length === 0) {
          alert('נא לבחור לפחות יום אחד בשבוע');
          return;
        }

        const dates = generateRecurringDates(
          formData.training_date,
          formData.end_date,
          formData.weekdays
        );

        // Check for duplicates
        const duplicates = dates.filter(date => {
          return sessions.some(session =>
            session.training_date === date &&
            session.training_time.substring(0, 5) === formData.training_time
          );
        });

        if (duplicates.length > 0) {
          const confirm = window.confirm(
            `נמצאו ${duplicates.length} אימונים קיימים באותה שעה. האם להמשיך ולדלג עליהם?`
          );
          if (!confirm) {
            return;
          }
        }

        let successCount = 0;
        let failCount = 0;

        for (const date of dates) {
          try {
            await trainingAPI.createSession({
              training_date: date,
              training_time: formData.training_time,
              training_type: formData.training_type,
              max_participants: formData.max_participants,
            });
            successCount++;
          } catch (error) {
            console.error(`Failed to create session for ${date}:`, error);
            failCount++;
          }
        }

        alert(
          `נוצרו ${successCount} אימונים בהצלחה` +
            (failCount > 0 ? `. ${failCount} נכשלו (ייתכן שכבר קיימים)` : '')
        );
      } else {
        // Create single session - check for duplicate
        const duplicate = sessions.find(
          session =>
            session.training_date === formData.training_date &&
            session.training_time.substring(0, 5) === formData.training_time
        );

        if (duplicate) {
          alert(
            `שגיאה: כבר קיים אימון בתאריך ${formData.training_date} בשעה ${formData.training_time}`
          );
          return;
        }

        await trainingAPI.createSession(formData);
        alert('האימון נוצר בהצלחה');
      }

      handleCloseModal();
      fetchSessions();
    } catch (error) {
      console.error('Error saving session:', error);
      alert(error.response?.data?.error || 'שגיאה בשמירת האימון');
    }
  };

  const handleDeleteSession = async (date, time) => {
    if (!window.confirm('האם אתה בטוח שברצונך למחוק את האימון?')) {
      return;
    }

    try {
      await trainingAPI.deleteSession(date, time);
      alert('האימון נמחק בהצלחה');
      fetchSessions();
    } catch (error) {
      console.error('Error deleting session:', error);
      alert('שגיאה במחיקת האימון');
    }
  };

  const groupSessionsByDate = (sessions) => {
    const filtered = filterDate
      ? sessions.filter((s) => s.training_date === filterDate)
      : sessions;

    const grouped = {};
    filtered.forEach((session) => {
      if (!grouped[session.training_date]) {
        grouped[session.training_date] = [];
      }
      grouped[session.training_date].push(session);
    });

    // Sort by date
    return Object.keys(grouped)
      .sort()
      .map((date) => ({
        date,
        sessions: grouped[date].sort((a, b) =>
          a.training_time.localeCompare(b.training_time)
        ),
      }));
  };

  const groupedSessions = groupSessionsByDate(sessions);

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="admin-spinner"></div>
        <p>טוען אימונים...</p>
      </div>
    );
  }

  return (
    <div className="admin-sessions">
      <div className="admin-header">
        <div>
          <h1>ניהול אימונים</h1>
          <p>סך הכל {sessions.length} אימונים</p>
        </div>
        <button className="admin-button-primary" onClick={() => handleOpenModal()}>
          ➕ הוסף אימון חדש
        </button>
      </div>

      {/* Filter */}
      <div className="admin-search-bar">
        <input
          type="date"
          value={filterDate}
          onChange={(e) => setFilterDate(e.target.value)}
          className="admin-search-input"
        />
        {filterDate && (
          <button
            className="admin-button-secondary"
            onClick={() => setFilterDate('')}
          >
            נקה סינון
          </button>
        )}
      </div>

      {/* Sessions by Date */}
      <div className="admin-sessions-list">
        {groupedSessions.map(({ date, sessions }) => (
          <div key={date} className="admin-date-group">
            <h2 className="admin-date-header">
              {formatDate(date, true)}
            </h2>

            <div className="admin-sessions-grid">
              {sessions.map((session, index) => (
                <div key={index} className="admin-session-card">
                  <div className="admin-session-header">
                    <div className="admin-session-time">
                      ⏰ {formatTime(session.training_time)}
                    </div>
                    <span className={trainingTypeBadgeClass(session.training_type)}>
                      {session.training_type}
                    </span>
                  </div>

                  <div className="admin-session-info">
                    <div className="admin-session-capacity">
                      <span>👥 משתתפים:</span>
                      <span className="admin-capacity-numbers">
                        {session.current_participants} / {session.max_participants}
                      </span>
                    </div>

                    <div className="admin-progress-bar">
                      <div
                        className="admin-progress-fill"
                        style={{
                          width: `${
                            (session.current_participants / session.max_participants) *
                            100
                          }%`,
                        }}
                      ></div>
                    </div>
                  </div>

                  <div className="admin-session-actions">
                    <button
                      className="admin-button-edit"
                      onClick={() => handleOpenModal(session)}
                    >
                      ✏️ ערוך
                    </button>
                    <button
                      className="admin-button-delete"
                      onClick={() =>
                        handleDeleteSession(
                          session.training_date,
                          session.training_time
                        )
                      }
                    >
                      🗑️ מחק
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        {groupedSessions.length === 0 && (
          <div className="admin-empty-state">
            <p>לא נמצאו אימונים</p>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="admin-modal-overlay" onClick={handleCloseModal}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2>{editingSession ? 'ערוך אימון' : 'אימון חדש'}</h2>
              <button className="admin-modal-close" onClick={handleCloseModal}>
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="admin-form">
              {!editingSession && (
                <div className="admin-form-group">
                  <label className="admin-checkbox-label">
                    <input
                      type="checkbox"
                      name="is_recurring"
                      checked={formData.is_recurring}
                      onChange={handleInputChange}
                    />
                    <span>אימון חוזר (צור אימונים מרובים)</span>
                  </label>
                </div>
              )}

              <div className="admin-form-row">
                <div className="admin-form-group">
                  <label>{formData.is_recurring ? 'תאריך התחלה *' : 'תאריך *'}</label>
                  <input
                    type="date"
                    name="training_date"
                    value={formData.training_date}
                    onChange={handleInputChange}
                    disabled={!!editingSession}
                    required
                  />
                  {editingSession && (
                    <small>לא ניתן לשנות תאריך של אימון קיים</small>
                  )}
                </div>

                {formData.is_recurring && !editingSession && (
                  <div className="admin-form-group">
                    <label>תאריך סיום *</label>
                    <input
                      type="date"
                      name="end_date"
                      value={formData.end_date}
                      onChange={handleInputChange}
                      min={formData.training_date}
                      required
                    />
                  </div>
                )}

                {(!formData.is_recurring || editingSession) && (
                  <div className="admin-form-group">
                    <label>שעה *</label>
                    <input
                      type="time"
                      name="training_time"
                      value={formData.training_time}
                      onChange={handleInputChange}
                      disabled={!!editingSession}
                      required
                    />
                    {editingSession && (
                      <small>לא ניתן לשנות שעה של אימון קיים</small>
                    )}
                  </div>
                )}
              </div>

              {formData.is_recurring && !editingSession && (
                <div className="admin-form-group">
                  <label>שעה *</label>
                  <input
                    type="time"
                    name="training_time"
                    value={formData.training_time}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              )}

              {formData.is_recurring && !editingSession && (
                <div className="admin-form-group">
                  <label>ימים בשבוע *</label>
                  <div className="admin-weekday-picker">
                    {[
                      { value: 0, label: 'א׳', fullLabel: 'ראשון' },
                      { value: 1, label: 'ב׳', fullLabel: 'שני' },
                      { value: 2, label: 'ג׳', fullLabel: 'שלישי' },
                      { value: 3, label: 'ד׳', fullLabel: 'רביעי' },
                      { value: 4, label: 'ה׳', fullLabel: 'חמישי' },
                      { value: 5, label: 'ו׳', fullLabel: 'שישי' },
                      { value: 6, label: 'ש׳', fullLabel: 'שבת' },
                    ].map((day) => (
                      <label
                        key={day.value}
                        className={`admin-weekday-item ${formData.weekdays.includes(day.value) ? 'selected' : ''}`}
                        title={day.fullLabel}
                      >
                        <input
                          type="checkbox"
                          checked={formData.weekdays.includes(day.value)}
                          onChange={(e) => {
                            const dayValue = day.value;
                            setFormData((prev) => ({
                              ...prev,
                              weekdays: e.target.checked
                                ? [...prev.weekdays, dayValue]
                                : prev.weekdays.filter((d) => d !== dayValue),
                            }));
                          }}
                        />
                        <span className="admin-weekday-label">{day.label}</span>
                      </label>
                    ))}
                  </div>
                  <small>{formData.weekdays.length} ימים נבחרו</small>
                </div>
              )}

              <div className="admin-form-row">
                <div className="admin-form-group">
                  <label>סוג אימון *</label>
                  <select
                    name="training_type"
                    value={formData.training_type}
                    onChange={handleInputChange}
                    required
                  >
                    {trainingTypes.length > 0 ? (
                      trainingTypes.map((t) => (
                        <option key={t.id} value={t.name}>
                          {t.name}
                        </option>
                      ))
                    ) : (
                      <>
                        <option value="בר">בר</option>
                        <option value="מכשיר">מכשיר</option>
                        <option value="מזרן">מזרן</option>
                        <option value="בהריון">בהריון</option>
                      </>
                    )}
                  </select>
                </div>

                <div className="admin-form-group">
                  <label>מקסימום משתתפים *</label>
                  <input
                    type="number"
                    name="max_participants"
                    value={formData.max_participants}
                    onChange={handleInputChange}
                    min="1"
                    max="20"
                    required
                  />
                </div>
              </div>

              <div className="admin-modal-footer">
                <button
                  type="button"
                  className="admin-button-secondary"
                  onClick={handleCloseModal}
                >
                  ביטול
                </button>
                <button type="submit" className="admin-button-primary">
                  {editingSession ? 'עדכן' : 'צור'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminSessions;
